// @ts-nocheck

import classNames from 'classnames';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import React, { PureComponent } from 'react';

import I13nLink from './I13nLink';

// These are props we will be using to detemine characteristics of the button
// and should not therefore not be passed to through to the child.
// We will omit these before passing all other props through to child.
const BLACKLIST_TO_CHILDREN_PROPS = [
  'type',
  'selected',
  'size',
  'disabled',
  'className',
  'loading',
  'testid',
  'newTab',
  'target',
  'rel',
  'accessDenied',
  'nativeButtonType',
  'showOnAccessDenied',
];

class Button extends PureComponent {
  static propTypes = {
    /**
     * Only required Button prop. Will be prefixed with 'btn-' when applied.
     */
    testid: PropTypes.string.isRequired,
    /**
     * Options: 'standard' (default), 'medium', 'small', 'no-miw' (to apply only
     * styling that there should be no min width set for the button).
     * Applies a classname corresponding to the size.
     */
    size: PropTypes.oneOf(['standard', 'medium', 'small', 'no-miw']),
    /**
     * Options: 'primary' (default), 'secondary', 'tertiary', 'dashed', 'danger'.
     * Applies a classname corresponding to the type. If the
     * 'disabled' prop is true AND the button renders to an <a> tag,
     * the type__disabled class is applied INSTEAD of the regular type class.
     */
    type: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'dashed', 'danger']),
    /**
     * If passed in, button will represent an external link and be rendered as
     * an <a> tag. The 'newTab' prop is only relevant in this scenario.
     */
    href: PropTypes.string,
    /**
     * When 'href' prop is passed in and 'newTab' prop is true, will apply
     * target="_blank", as well as rel="noopener noreferrer" for security.
     */
    newTab: PropTypes.bool,
    /**
     * If passed in, button will represent an internal link and be rendered
     * ultimately as an <a> tag. Utilizes 'react-router's 'Link' component.
     */
    to: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        pathname: PropTypes.string,
        query: PropTypes.object,
        hash: PropTypes.string,
      }),
    ]),
    /**
     * If button renders to a <button> tag (has no 'href' or 'to' property),
     * applies disabled state. If renders to an <a> tag, passing 'disabled' prop
     * as true will mean the corresponding type__disabled class is applied
     * instead of regular type class. The same styling is applied in either the
     * <button> or <a> scenario.
     */
    disabled: PropTypes.bool,
    /**
     * Applies a `{type}-selected` classname. Currently there only existing
     * selected styling for the 'secondary' type (is benign for other types).
     * Used for example in BinaryToggle component, where 'Yes' and 'No' buttons
     * are present and the user is selecting one or the other.
     */
    selected: PropTypes.bool,
    /**
     * Shows loading spinner within button and disables button
     */
    loading: PropTypes.bool,

    /**
     * Should button be disabled because access is denied?
     * If true a lock icon is added on the button along with a message on hover
     */
    accessDenied: PropTypes.bool,

    /**
     * Passes this as a type to the native HTML element only if it doesn't have
     * `to` or `href`
     * @type {String}
     */
    nativeButtonType: PropTypes.string,
    /**
     * custom text or component to show instead of regular this.props.children + lock when access is denied
     */
    showOnAccessDenied: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    /**
     * Any other props are passed straight through to the child
     */
    children: PropTypes.any,
    onClick: PropTypes.func,
    className: PropTypes.string,
    title: PropTypes.any,
    style: PropTypes.object,
  };

  static defaultProps = {
    type: 'primary',
    selected: false,
    size: 'standard',
    disabled: false,
    loading: false,
    newTab: true,
    nativeButtonType: 'button',
  };

  render() {
    const { type, selected, size, loading, onClick, accessDenied, nativeButtonType, showOnAccessDenied } = this.props;

    const accessDeniedLock = (
      <i
        className={classNames('material-icons h_mstart-5', {
          [`button-font-size-${size}`]: !!size,
        })}
      >
        lock
      </i>
    );

    const children = accessDenied ? (
      showOnAccessDenied ? (
        <div>{showOnAccessDenied}</div>
      ) : (
        <div className="access-denied-wrapper">
          {this.props.children}
          {accessDeniedLock}
        </div>
      )
    ) : (
      this.props.children
    );

    // button should be disable when loading also
    const disabled = accessDenied || this.props.disabled || this.props.loading;

    // as <a> tags do not have a disabled property, we need to apply as a class
    const willRenderToDisabledAnchorTag = (this.props.href || this.props.to) && disabled;

    const className = classNames('button', {
      [`button-type-${type}`]: !willRenderToDisabledAnchorTag,
      [`button-type-${type}__disabled`]: willRenderToDisabledAnchorTag,
      [`${type}-selected`]: selected,
      [`button-size-${size}`]: !!size,
      [this.props.className]: !!this.props.className,
      button__loading: !!loading,
      'btn-icon-with-text': accessDenied,
    });

    const childProps = {
      disabled,
      className,
      'data-testid': `btn-${this.props.testid}`,
      ...R.omit(BLACKLIST_TO_CHILDREN_PROPS, { ...this.props }),
      ...{ onClick: disabled ? null : onClick },
    };

    // external link
    if (this.props.href) {
      return (
        <a {...childProps} target={this.props.newTab ? '_blank' : ''} rel={this.props.newTab ? 'noopener noreferrer' : ''}>
          {children}
        </a>
      );
    }

    // internal link route
    if (this.props.to) {
      return <I13nLink {...childProps}>{children}</I13nLink>;
    }

    // button triggering a function only (no routing change)
    return (
      <button type={nativeButtonType} {...childProps}>
        {children}
      </button>
    );
  }
}

export default Button;
